import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Youtube, Loader2, Upload, Search, Play, Trash2, ExternalLink, Quote, X } from "lucide-react";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDur = (s) => {
  if (!s) return "—";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};
const fmtTs = (s) => fmtDur(s);

function highlight(text, q) {
  if (!q || q.length < 2) return text;
  try {
    const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${safe})`, "ig"));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase() ? (
        <mark key={`m-${i}-${p}`} className="bg-[color:var(--papicoo-gold)]/40 rounded px-0.5">{p}</mark>
      ) : <span key={`s-${i}-${p}`}>{p}</span>
    );
  } catch { return text; }
}

export default function NihmaTV() {
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  // Form ingest
  const [ytUrl, setYtUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  const load = () => axios.get(`${API}/nihma/videos`).then((r) => setVideos(r.data || [])).catch(() => {});

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    setLoadingDetail(true);
    axios.get(`${API}/nihma/video/${selected}`)
      .then((r) => setDetail(r.data))
      .catch(() => toast.error(T("Vidéo introuvable")))
      .finally(() => setLoadingDetail(false));
  }, [selected]);

  const submitYt = async (e) => {
    e.preventDefault();
    if (!ytUrl.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/nihma/transcribe`, { url: ytUrl.trim(), language: "fr" }, { timeout: 180000 });
      toast.success(`Transcrit : ${T(data.title)}`);
      setYtUrl("");
      await load();
      setSelected(data.video_id);
    } catch (err) {
      const msg = err?.response?.data?.detail || T("Échec de la transcription.");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || uploadBusy) return;
    if (f.size > 22 * 1024 * 1024) {
      toast.error(T("Fichier > 22 MB. Compresse-le ou découpe-le."));
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("language", "fr");
    fd.append("title", f.name.replace(/\.[^.]+$/, ""));
    setUploadBusy(true);
    try {
      const { data } = await axios.post(`${API}/nihma/transcribe-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(`Audio transcrit : ${T(data.title)}`);
      await load();
      setSelected(data.video_id);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      const msg = err?.response?.data?.detail || T("Échec transcription.");
      toast.error(msg);
    } finally { setUploadBusy(false); }
  };

  const filtered = q
    ? videos.filter((v) => (v.title || "").toLowerCase().includes(q.toLowerCase()) || (v.uploader || "").toLowerCase().includes(q.toLowerCase()))
    : videos;

  // Search inside transcript
  const transcriptMatches = (() => {
    if (!detail || !q || q.length < 2) return null;
    const segs = detail.segments || [];
    const needle = q.toLowerCase();
    return segs.filter((s) => (s.text || "").toLowerCase().includes(needle));
  })();

  return (
    <div data-testid="page-nihma" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Nihma TV · transcriptions IA")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Les enseignements,")}<br />
        <span className="italic font-light">{T("à portée de recherche.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-3xl">
        {T("Colle l'URL d'une vidéo Nihma TV (ou n'importe quelle vidéo YouTube), Papicoo extrait l'audio et le transcrit en français via Whisper. Tu peux ensuite chercher un mot précis et atterrir au timecode exact.")}
      </p>

      {/* Ingestion */}
      <div className="mt-7 grid lg:grid-cols-2 gap-4">
        <form onSubmit={submitYt} className="papicoo-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)] mb-2 inline-flex items-center gap-2">
            <Youtube size={14} /> {T("Depuis YouTube")}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="nihma-url-input"
              value={ytUrl}
              onChange={(e) => setYtUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="flex-1 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />
            <button
              data-testid="nihma-submit-url"
              type="submit"
              disabled={busy || !ytUrl.trim()}
              className="px-4 py-2.5 rounded-lg papicoo-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Transcrire
            </button>
          </div>
          <div className="text-[11px] italic text-[color:var(--papicoo-muted)] mt-2">
            {T("Durée recommandée : moins de 15 min (limite Whisper 25 MB). Si YouTube refuse le download, utilise l'upload audio.")}
          </div>
        </form>

        <div className="papicoo-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)] mb-2 inline-flex items-center gap-2">
            <Upload size={14} /> {T("Plan B — fichier audio")}
          </div>
          <label className="flex flex-col sm:flex-row gap-2">
            <input
              ref={fileRef}
              data-testid="nihma-file-input"
              type="file"
              accept=".mp3,.m4a,.wav,.webm,audio/*"
              onChange={submitFile}
              disabled={uploadBusy}
              className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--papicoo-ink)] file:text-[color:var(--papicoo-sand)] file:cursor-pointer"
            />
            {uploadBusy && <Loader2 size={16} className="animate-spin text-[color:var(--papicoo-muted)] self-center" />}
          </label>
          <div className="text-[11px] italic text-[color:var(--papicoo-muted)] mt-2">
            {T("Formats : mp3, m4a, wav, webm. Max 22 MB.")}
          </div>
        </div>
      </div>

      {/* Search box */}
      <div className="mt-7 relative max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--papicoo-muted)]" />
        <input
          data-testid="nihma-search"
          placeholder={selected ? T("Cherche un mot dans la transcription…") : T("Filtrer les vidéos par titre…")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-white border border-[color:var(--papicoo-border)] rounded-full pl-9 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
        />
        {q && (
          <button data-testid="nihma-clear-search" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Liste / détail */}
      {!selected ? (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-sm italic text-[color:var(--papicoo-muted)] py-12 papicoo-card">
              {T("Aucune vidéo transcrite pour l'instant. Colle une URL ou uploade un audio ci-dessus.")}
            </div>
          )}
          {filtered.map((v) => (
            <button
              key={v.video_id}
              data-testid={`nihma-card-${v.video_id}`}
              onClick={() => setSelected(v.video_id)}
              className="papicoo-card text-left overflow-hidden hover:-translate-y-0.5 transition-transform"
            >
              {v.thumbnail ? (
                <div className="aspect-video bg-[color:var(--papicoo-sand-deep)]" style={{ backgroundImage: `url(${v.thumbnail})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              ) : (
                <div className="aspect-video bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-gold)] flex items-center justify-center font-display text-3xl">N</div>
              )}
              <div className="p-3.5">
                <div className="font-display text-[15px] leading-tight text-[color:var(--papicoo-ink)] line-clamp-2">{T(v.title)}</div>
                <div className="text-[11px] text-[color:var(--papicoo-muted)] mt-1.5 flex items-center gap-2">
                  <span>{v.uploader || "Nihma TV"}</span>
                  <span>·</span>
                  <span>{fmtDur(v.duration)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-6">
          <button data-testid="nihma-back" onClick={() => { setSelected(null); setDetail(null); setQ(""); }} className="text-sm papicoo-link-underline mb-4">{T("← Toutes les vidéos")}</button>
          {loadingDetail && <div className="text-center py-12"><Loader2 className="animate-spin text-[color:var(--papicoo-muted)] mx-auto" /></div>}
          {detail && (
            <div data-testid="nihma-detail" className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
              <div className="papicoo-card p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-2xl text-[color:var(--papicoo-ink)] leading-tight">{T(detail.title)}</div>
                    <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">{detail.uploader || "Nihma TV"} · {fmtDur(detail.duration)} · {detail.language?.toUpperCase()}</div>
                  </div>
                  {detail.webpage_url && (
                    <a href={detail.webpage_url} target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1 papicoo-link-underline shrink-0">
                      {T("YouTube")} <ExternalLink size={11} />
                    </a>
                  )}
                </div>

                {transcriptMatches !== null && (
                  <div className="text-xs text-[color:var(--papicoo-muted)] mb-3 italic">
                    {transcriptMatches.length === 0 ? T("Aucun segment pour « ") + q + " »" : `${transcriptMatches.length} ${T(transcriptMatches.length > 1 ? "segments pour" : "segment pour")} « ${q} »`}
                  </div>
                )}

                <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-1.5 text-sm leading-relaxed text-[color:var(--papicoo-ink)]">
                  {(detail.segments || []).length > 0 ? (
                    (transcriptMatches || detail.segments).map((s, i) => (
                      <div key={`${s.start}-${i}`} data-testid={`nihma-seg-${i}`} className="flex gap-3 group">
                        <span className="shrink-0 text-[11px] tabular-nums text-[color:var(--papicoo-accent)] pt-0.5 w-12 text-right">{fmtTs(s.start)}</span>
                        <p>{highlight(s.text, q)}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-[color:var(--papicoo-ink)] whitespace-pre-wrap">{highlight(detail.transcript || "", q)}</p>
                  )}
                </div>
              </div>

              <div className="papicoo-card p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-accent)] mb-2 inline-flex items-center gap-2">
                  <Quote size={12} /> {T("Citation à partager")}
                </div>
                <div className="text-sm italic text-[color:var(--papicoo-ink)] leading-relaxed">
                  « {(detail.transcript || "").slice(0, 280).trim() || "…"}{(detail.transcript || "").length > 280 ? "…" : ""} »
                </div>
                <button
                  data-testid="nihma-copy"
                  onClick={() => { navigator.clipboard.writeText(detail.transcript || ""); toast.success(T("Transcription copiée.")); }}
                  className="mt-4 text-xs px-3 py-1.5 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] hover:bg-[color:var(--papicoo-ink)]/90"
                >
                  {T("Copier la transcription complète")}
                </button>
                <a
                  download={`${detail.title.replace(/[^a-zA-Z0-9-_]/g, "_")}.txt`}
                  href={`data:text/plain;charset=utf-8,${encodeURIComponent(detail.transcript || "")}`}
                  data-testid="nihma-download"
                  className="mt-2 inline-block text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/40 ml-2"
                >
                  {T("Télécharger .txt")}
                </a>

                {/* Carrousel Instagram */}
                {(detail.segments || []).length > 0 && (
                  <div className="mt-5 pt-5 border-t border-[color:var(--papicoo-border)]">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-accent)] mb-2 inline-flex items-center gap-1">{T("📱 Carrousel Instagram")}</div>
                    <p className="text-xs text-[color:var(--papicoo-muted)] leading-relaxed mb-3">
                      {T("Génère un carrousel prêt à poster (cover + jusqu'à 8 segments + CTA papicoo.com).")}
                    </p>
                    <button
                      data-testid="nihma-carousel-btn"
                      onClick={async () => {
                        const segs = (detail.segments || []).slice(0, 8).map((_, i) => i);
                        toast.info(T("Génération du carrousel…"));
                        try {
                          const resp = await fetch(`${API}/nihma/carousel`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ video_id: detail.video_id, segments: segs }),
                          });
                          if (!resp.ok) throw new Error("HTTP " + resp.status);
                          const blob = await resp.blob();
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(blob);
                          a.download = `papicoo-carrousel-${detail.video_id}.zip`;
                          a.click();
                          URL.revokeObjectURL(a.href);
                          toast.success(T("Carrousel téléchargé 🌹"));
                        } catch (e) { toast.error(T("Échec génération carrousel.")); }
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full papicoo-btn-gold text-sm"
                    >
                      {T("Télécharger le carrousel ZIP")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
